import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Button, Loader, Message } from '@getro/rombo';
import { Box, Flex, Text } from 'rebass/styled-components';
import { activeNetworkSelector } from 'redux/selectors';
import { useDispatch, useSelector } from 'react-redux';
import { useGetChargeBessSessionMutation, useGetSubscriptionsQuery } from 'services/organizationsV2';

import useFeatureToggle from 'hooks/useFeatureToggle';

import { SettingsLayoutHeader } from '../../../components/atoms/settingsLayoutHeader';
import UpgradeModal from '../../../components/organisms/upgradeModal';
import usePageHook from '../../../hooks/usePageHook';
import { trackPlansAndBilling } from '../actions';
import { PlansBox } from './plansBox';
import { AICreditsSection } from './components/AICreditsSection';

const DEFAULT_BILLING_SUPPORT_EMAIL = 'billing@findem.ai';
const pageTitle = 'Plans and billing';

export const PlansAndBilling = () => {
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [upgradeFeatureName, setUpgradeFeatureName] = useState('contacts_limit');
  const network = useSelector(activeNetworkSelector);
  const availableAgentCredits = network?.availableAgentCredits || 0;
  const [getChargeBessSession] = useGetChargeBessSessionMutation();
  const { data: subscriptions, error, isLoading } = useGetSubscriptionsQuery(network.id);
  const dispatch = useDispatch();
  const cbInstance = useRef(null);
  const sourcingAgentEnabled = network?.features?.includes('sourcing_agent');
  const isBillingGatingEnabled = useFeatureToggle('billingGating');

  const displayedSubscriptions = useMemo(() => {
    if (!subscriptions?.activeSubscriptions) return [];
    if (!isBillingGatingEnabled) return subscriptions.activeSubscriptions;
    return subscriptions.activeSubscriptions.filter((subscription) =>
      subscription.subscriptionItems?.some((item) => item.amount > 0),
    );
  }, [subscriptions, isBillingGatingEnabled]);

  const billingEligible = useMemo(() => {
    if (!isBillingGatingEnabled) return true;
    if (error) return true;
    if (isLoading) return true;
    return displayedSubscriptions.length > 0;
  }, [isBillingGatingEnabled, error, isLoading, displayedSubscriptions]);

  const billingSupportEmail = subscriptions?.billingSupportEmail || DEFAULT_BILLING_SUPPORT_EMAIL;

  const canAddCompanies = useMemo(() => {
    const sub = subscriptions?.activeSubscriptions?.find((subscription) =>
      subscription.subscriptionItems?.find((item) => item?.stairstep?.unit === 'companies'),
    ); // eslint-disable-line function-paren-newline

    if (sub) {
      const activeStairstep = sub.subscriptionItems?.find((item) => item?.stairstep?.unit === 'companies');

      if (activeStairstep) {
        return activeStairstep?.stairstep?.used < activeStairstep?.stairstep?.total;
      }
    }

    return true;
  }, [subscriptions]);

  usePageHook(pageTitle);

  useEffect(() => {
    const chargebeeSite = import.meta.env.VITE_CHARGEBEE_SITE;

    if (cbInstance.current || !network.isManager || !billingEligible || isLoading || !chargebeeSite) {
      return () => {};
    }

    try {
      cbInstance.current = window.Chargebee.init({
        site: chargebeeSite,
      });

      cbInstance.current.setPortalSession(async () => {
        const { data } = await getChargeBessSession(network.id);
        return data;
      });
    } catch {
      // Chargebee init failed, skip portal
    }

    return () => {};
  }, [getChargeBessSession, network, billingEligible, isLoading]);

  useEffect(
    () => () => {
      if (cbInstance.current) {
        cbInstance.current.logout();
        cbInstance.current = null;
      }
    },
    [],
  );

  if (!network.isManager) return null;

  const SECTION_EVENT_MAP = {
    PAYMENT_SOURCES: 'open_update_payment_method_widget',
    ADDRESS: 'open_update_billing_info_widget',
    BILLING_HISTORY: 'open_billing_history_widget',
  };

  const handlePortal = (sectionKey) => {
    if (!cbInstance.current || !window.Chargebee) return;

    const sections = window.Chargebee.getPortalSections();
    const sectionType = sections[sectionKey];

    cbInstance.current.createChargebeePortal().openSection({ sectionType });

    const eventName = SECTION_EVENT_MAP[sectionKey];
    if (eventName) {
      dispatch(trackPlansAndBilling({ eventName }));
    }
  };
  const { connectTier } = network;

  const noConnectSubscription =
    !isLoading &&
    (error ||
      !subscriptions?.activeSubscriptions?.length ||
      !subscriptions?.activeSubscriptions?.find((subscription) =>
        subscription.subscriptionItems?.find(
          (item) => item?.itemPriceFamilyId === 'GetroConnect' || item?.stairstep?.unit === 'contacts',
        ),
      )); // eslint-disable-line function-paren-newline

  return (
    <Box maxWidth="600px">
      <SettingsLayoutHeader
        title={pageTitle}
        description="See your current plan and update payment and billing information."
      />
      {isLoading && (
        <Flex mt="32px" justifyContent="center" width="100%">
          <Loader />
        </Flex>
      )}
      {!isLoading && isBillingGatingEnabled && !billingEligible && (
        <Box
          mt="40px"
          p="24px"
          sx={{
            borderRadius: '4px',
            border: '1px solid',
            borderColor: 'border.subtle',
            fontSize: '14px',
            textAlign: 'center',
          }}
        >
          <Text mb="8px" display="block">
            Billing details are not currently available here.
          </Text>
          <Text>
            For invoices or billing questions, please contact{' '}
            <Text as="a" href={`mailto:${billingSupportEmail}`} color="text.main" fontWeight="medium">
              {billingSupportEmail}
            </Text>
            .
          </Text>
        </Box>
      )}
      {billingEligible && (
        <>
          {!isLoading && sourcingAgentEnabled && (
            <Box mt="32px">
              <AICreditsSection availableCredits={availableAgentCredits} />
            </Box>
          )}
          {noConnectSubscription && (
            <Box mt="32px">
              <PlansBox
                subscription={{
                  quantity: 1,
                  unitPrice: 0,
                  amount: 0,
                  freeQuantity: network.contactsLimit,
                  itemPriceFamilyId: 'GetroConnect',
                  stairstep: { unit: 'contacts', total: network.contactsLimit, used: network.contactsTotal },
                }}
                connectTier={connectTier}
                networkSlug={network.slug}
                billingPeriod={1}
                billingPeriodUnit="month"
                currencyCode="USD"
                upcomingInvoiceTotal={0}
                status="active"
              />
            </Box>
          )}
          {!isLoading && displayedSubscriptions.length > 0 && (
            <Flex mt="32px" sx={{ flexDirection: 'column', gap: '16px' }}>
              {displayedSubscriptions.map((subscription) => (
                <PlansBox
                  key={subscription.id}
                  subscription={subscription.subscriptionItems[0]}
                  connectTier={connectTier}
                  networkSlug={network.slug}
                  billingPeriod={subscription.billingPeriod}
                  billingPeriodUnit={subscription.billingPeriodUnit}
                  currencyCode={subscription.currencyCode}
                  upcomingInvoiceTotal={subscription.upcomingInvoiceTotal}
                  nextBillingAt={subscription.nextBillingAt}
                  coupon={subscription.coupon}
                  status={subscription.status}
                  currentTermEnd={subscription.currentTermEnd}
                />
              ))}
            </Flex>
          )}
          {network.contactsOverLimit > 0 && (
            <Message type="warning" mt="16px">
              <Text fontSize="14px" fontWeight="medium" color="text.dark">
                Your network has reached its contacts limit
              </Text>
              <Text fontSize="14px" mt="8px">
                Upgrade now to keep growing your network and make sure high-impact intros are not missed.
              </Text>
              <Button
                size="small"
                mt="8px"
                onClick={() => {
                  setUpgradeFeatureName('contacts_limit');
                  setShowUpgradeModal(true);
                }}
              >
                Upgrade contacts limit
              </Button>
            </Message>
          )}
          {!canAddCompanies && (
            <Message type="warning" mt="16px">
              <Text fontSize="14px" fontWeight="medium" color="text.dark">
                Your job board has reached its company limit
              </Text>
              <Text fontSize="14px" mt="8px">
                Upgrade now to keep growing your network and make sure high-impact intros are not missed.
              </Text>
              <Button
                size="small"
                mt="8px"
                onClick={() => {
                  setUpgradeFeatureName('companies_limit');
                  setShowUpgradeModal(true);
                }}
              >
                Upgrade company limit
              </Button>
            </Message>
          )}
          {!isLoading && (
            <>
              <Flex
                mt="32px"
                flexDirection={['column', 'row']}
                sx={{ gap: '16px', a: { color: 'text.main', fontSize: '14px', lineHeight: '16px' } }}
              >
                <Box as="a" onClick={() => handlePortal('PAYMENT_SOURCES')} data-cb-type="portal">
                  Update payment method
                </Box>
                <Box as="a" onClick={() => handlePortal('ADDRESS')}>
                  Update billing information
                </Box>
                <Box as="a" onClick={() => handlePortal('BILLING_HISTORY')}>
                  Billing history
                </Box>
              </Flex>
            </>
          )}
        </>
      )}
      <UpgradeModal
        isOpen={showUpgradeModal}
        onClose={() => setShowUpgradeModal(false)}
        featureName={upgradeFeatureName}
      />
    </Box>
  );
};

export default PlansAndBilling;
